// The page's text in the reader's language (docs/i18n.md). Every piece of UI text is a key in the catalogs
// (public/locales/<lang>.json, flat "area.thing" keys); code asks for it by key:
//   import { t, onLang } from '/i18n.js';
//   t('bar.chat')                         -> 聊天 / Chat
//   t('floors.count', { n: 3 })           -> ICU-lite: {name}, {n, number}, {n, plural, ...}, {x, select, ...} (i18n-core.js)
//   onLang(() => redraw())                 the language changed (设置 → 语言): draw your text again
//   tParts('key', { link: a })            text with an element in it: ['Words from ', a, '.']
//   tr(el, 'key', vars)                    el.textContent = t(key), and again by itself on every change (data-i18n)
//   fmtNumber(n) fmtTime(d) fmtDate(d) fmtRelative(d)   numbers, times, "3分钟前" / "3 min. ago" (Intl)
//   errorText('floors.err', { code, message })   a server error: its code's text here, else the server's message
// Which language: the reader's choice in 设置 (localStorage deck.lang), else their Raft account's (useAccountLocale(),
// from /me, when Raft says), else the browser's, else 简体中文. The catalog for it loads before any importer runs (a
// top-level await: one small JSON file, precached by the service worker). Many modules import it and share one
// instance (the build bundles it once); each catalog's URL below is its content-hashed file (vite.config.mjs).
import { format, parse, argNames } from '/i18n-core.js';

/** The languages there are a catalog for (each one's name is the key lang.name.<id>, in its own language). */
export const LANGS = ['zh-CN', 'en'];
// each catalog by its content-hashed URL (vite.config.mjs), so a deploy that changes only the text is a new version too
const URLS = { 'zh-CN': new URL('./locales/zh-CN.json', import.meta.url).pathname, en: new URL('./locales/en.json', import.meta.url).pathname };
const PREF = 'deck.lang';
const DEFAULT = 'zh-CN';

/** The best language we have for a BCP 47 tag (zh, zh-Hans-CN, zh-TW → zh-CN; en-GB → en), or null. */
export function match(tag) {
  if (typeof tag !== 'string' || !tag) return null;
  const t = tag.toLowerCase();
  return LANGS.find((l) => l.toLowerCase() === t) ?? LANGS.find((l) => l.split('-')[0] === t.split('-')[0]) ?? null;
}
const stored = () => { try { const v = localStorage.getItem(PREF); return v && match(v) ? match(v) : null; } catch { return null; } };
let account = null; // the Raft account's locale (useAccountLocale), when it said one
const browser = () => { for (const l of (typeof navigator !== 'undefined' && (navigator.languages?.length ? navigator.languages : [navigator.language])) || []) { const m = match(l); if (m) return m; } return null; };
const resolve = () => stored() ?? account ?? browser() ?? DEFAULT;

const catalogs = {};
// Every importer waits for the first load (the whole page does): each try gives up after 5 s, and a failed one is
// tried once more, so a stalled request can't keep the page from starting.
async function load(l) {
  if (catalogs[l]) return catalogs[l];
  for (let i = 0; i < 2 && !catalogs[l]; i++) {
    try { const r = await fetch(URLS[l], { signal: AbortSignal.timeout?.(5000) }); if (!r.ok) throw Error(String(r.status)); catalogs[l] = await r.json(); }
    catch (e) { console.error('i18n: no catalog for', l, e); catalogs[l] = null; }
  }
  return catalogs[l];
}

let uiFontNow = null; // uiFont()'s, for the language now
/** The language the page shows now (a LANGS id). */
export let lang = resolve();
if (!(await load(lang)) && lang !== DEFAULT) { lang = DEFAULT; await load(DEFAULT); } // no catalog: 简体中文 rather than keys
setHtmlLang();
function setHtmlLang() { if (typeof document !== 'undefined') document.documentElement.lang = lang; uiFontNow = null; }
/** The UI font family for canvas text (index.html --ui-font): not in Chinese, it names Simplified Chinese fonts, or a
 *  Japanese one would draw the Han characters still on the page (names, chat). ctx.font = `12px ${uiFont()}`. */
export function uiFont() {
  if (uiFontNow == null) { try { uiFontNow = getComputedStyle(document.documentElement).getPropertyValue('--ui-font').trim(); } catch {} uiFontNow ||= '-apple-system,system-ui,sans-serif'; }
  return uiFontNow;
}

/** key's text in the current language, vars filled in. A missing key shows as the key itself (the check prevents it). */
export function t(key, vars) {
  const msg = catalogs[lang]?.[key] ?? catalogs[DEFAULT]?.[key];
  return msg === undefined ? key : format(msg, vars, lang);
}
/** key's text cut where its slot placeholders are, with each slot's value in its place: for text with a link or an
 *  element in the middle, wherever a language puts it. tParts('about.x', { link: a }) -> ['Words from ', a, '.'].
 *  vars: plain values as for t(). */
export function tParts(key, slots, vars) {
  const names = Object.keys(slots);
  const s = t(key, { ...vars, ...Object.fromEntries(names.map((n, i) => [n, `\u0001${i}\u0001`])) });
  return s.split(/\u0001(\d+)\u0001/).map((p, i) => (i % 2 ? slots[names[+p]] : p)).filter((p) => p !== '');
}
/** Is there a text for key? (e.g. a server code this page knows) */
export const has = (key) => catalogs[lang]?.[key] !== undefined || catalogs[DEFAULT]?.[key] !== undefined;

/** The text for a server error: `${prefix}.${code}` if this page has it (vars: the error's params), else the server's
 *  own message (an older page, a code added later), else `${prefix}.other`. err: {code, message?, params?}. */
export function errorText(prefix, err, vars) {
  const key = err?.code ? `${prefix}.${err.code}` : '';
  const all = { ...err?.params, ...vars };
  // (a server without the params this text wants, e.g. an older one after a rollback: its own message, not "{max}")
  if (key && has(key) && (!err.message || argNames(parse(catalogs[lang]?.[key] ?? catalogs[DEFAULT][key])).every((a) => all[a] !== undefined && all[a] !== null))) return t(key, all);
  return err?.message || t(`${prefix}.other`, vars);
}

const listeners = new Set();
/** fn() after every language change; returns a function that stops it. */
export function onLang(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/** The reader's choice: 'auto' (account, then browser) or a LANGS id. */
export function langPref() { return stored() ?? 'auto'; }
/** Switch (设置 → 语言): the catalog loaded, remembered on this device, then everything listening redraws. False if
 *  that language's catalog couldn't be loaded (nothing changes then). */
export async function setLang(pref) {
  const want = pref === 'auto' ? null : match(pref);
  if (want && !(await load(want))) return false;
  try { if (want) localStorage.setItem(PREF, want); else localStorage.removeItem(PREF); } catch {}
  await apply();
  return true;
}
/** The Raft account's locale (/me `locale`, when Raft has one): used when the reader hasn't picked a language. */
export async function useAccountLocale(tag) { account = match(tag); await apply(); }
async function apply() {
  const next = resolve();
  if (next === lang || !(await load(next))) return;
  if (next !== resolve()) return; // changed again while loading; that call applies
  lang = next;
  setHtmlLang();
  translateDom();
  for (const fn of [...listeners]) try { fn(lang); } catch (e) { console.error(e); }
  if (typeof dispatchEvent === 'function') dispatchEvent(new CustomEvent('deck:lang', { detail: { lang } }));
}

/** el.textContent = t(key, vars), kept up to date on language changes (data-i18n, data-i18n-vars). Returns el.
 *  attrs: other attributes to translate the same way, e.g. { title: 'bar.mic.tip', 'aria-label': 'bar.mic.label' }. */
export function tr(el, key, vars, attrs) {
  if (key) { el.dataset.i18n = key; el.textContent = t(key, vars); }
  if (vars) el.dataset.i18nVars = JSON.stringify(vars); else delete el.dataset.i18nVars;
  if (attrs) { el.dataset.i18nAttrs = JSON.stringify(attrs); for (const [a, k] of Object.entries(attrs)) el.setAttribute(a, t(k, vars)); }
  return el;
}
/** Every element under root marked by tr() (or written in HTML as data-i18n="key" / data-i18n-attrs='{"title":"key"}'),
 *  in the current language. Runs by itself on a change; call it for HTML you insert. */
export function translateDom(root = typeof document !== 'undefined' ? document : null) {
  if (!root) return;
  for (const el of root.querySelectorAll('[data-i18n],[data-i18n-attrs]')) {
    let vars; try { vars = el.dataset.i18nVars ? JSON.parse(el.dataset.i18nVars) : undefined; } catch {}
    if (el.dataset.i18n) el.textContent = t(el.dataset.i18n, vars);
    if (el.dataset.i18nAttrs) try { for (const [a, k] of Object.entries(JSON.parse(el.dataset.i18nAttrs))) el.setAttribute(a, t(k, vars)); } catch {}
  }
}

// ---- formatters (Intl, in the current language)
export const fmtNumber = (n, o) => new Intl.NumberFormat(lang, o).format(n);
/** 14:05 */
export const fmtTime = (d, o = { hour: '2-digit', minute: '2-digit' }) => new Intl.DateTimeFormat(lang, o).format(new Date(d));
/** 11月2日 / Nov 2 */
export const fmtDate = (d, o = { month: 'short', day: 'numeric' }) => new Intl.DateTimeFormat(lang, o).format(new Date(d));
const STEPS = [['second', 60], ['minute', 60], ['hour', 24], ['day', 7], ['week', 4.35], ['month', 12], ['year', Infinity]];
/** d relative to now (or to `now`), in Intl's words: 现在 / 3分钟前 / 2天后, now / 3 min. ago / in 2 days. */
export function fmtRelative(d, now = Date.now(), style = 'short') {
  let v = (new Date(d).getTime() - now) / 1000;
  const f = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style });
  if (Math.abs(v) < 45) return f.format(0, 'second');
  for (const [unit, n] of STEPS) { if (Math.abs(v) < n) return f.format(Math.round(v), unit); v /= n; }
  return f.format(Math.round(v), 'year');
}
